const RUC_WEIGHTS = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];

const normalizar = (value) => (value == null ? "" : String(value).trim());

export const validarRucEstructura = (ruc) => {
  const value = normalizar(ruc);
  if (!/^\d{11}$/.test(value)) return false;
  if (!/^1\d|^2\d/.test(value)) return false;

  const digits = value.split("").map(Number);
  const sum = RUC_WEIGHTS.reduce((acc, w, idx) => acc + w * digits[idx], 0);
  const remainder = sum % 11;
  const check = remainder === 1 ? 1 : remainder === 0 ? 0 : 11 - remainder;
  return check === digits[10];
};

export const validarComprobanteEstructura = ({ tipoComprobante, serie, numero }) => {
  const tipo = normalizar(tipoComprobante).toUpperCase();
  const s = normalizar(serie).toUpperCase();
  const n = normalizar(numero);

  if (!n || !/^\d{1,8}$/.test(n)) {
    return { ok: false, mensaje: "Número de comprobante inválido." };
  }

  if (tipo === "FACTURA" && !/^[F][A-Z0-9]{3}$/.test(s) && !/^\d{4}$/.test(s)) {
    return { ok: false, mensaje: "Serie de factura inválida (ej: F001 o 0001)." };
  }

  if (tipo === "RECIBO_HONORARIOS" && !/^[E][A-Z0-9]{3}$/.test(s) && !/^\d{4}$/.test(s)) {
    return { ok: false, mensaje: "Serie de recibo por honorarios inválida." };
  }

  if (tipo === "NOTA_CREDITO" && !/^[FBC][A-Z0-9]{3}$/.test(s) && !/^\d{4}$/.test(s)) {
    return { ok: false, mensaje: "Serie de nota de crédito inválida." };
  }

  return { ok: true, mensaje: "Estructura válida." };
};

export const validarSunat = async ({
  rucEmisor,
  rucReceptor,
  tipoComprobante,
  serie,
  numero,
  fechaEmision,
  total,
  moneda,
  xmlUrl,
  cdrUrl,
}) => {
  if (!validarRucEstructura(rucEmisor)) {
    return { ok: false, estado: "OBSERVADO", mensaje: "RUC del proveedor inválido." };
  }
  if (!validarRucEstructura(rucReceptor)) {
    return { ok: false, estado: "OBSERVADO", mensaje: "RUC de empresa inválido." };
  }

  const estructura = validarComprobanteEstructura({ tipoComprobante, serie, numero });
  if (!estructura.ok) {
    return { ok: false, estado: "OBSERVADO", mensaje: estructura.mensaje };
  }

  const endpoint = normalizar(process.env.REACT_APP_SUNAT_VALIDATION_URL);
  if (!endpoint) {
    return {
      ok: true,
      estado: "PENDIENTE",
      mensaje: "Validación estructural OK. Endpoint SUNAT no configurado.",
      fuente: "LOCAL",
      detalle: { xmlUrl: normalizar(xmlUrl), cdrUrl: normalizar(cdrUrl) },
    };
  }

  try {
    const token = normalizar(process.env.REACT_APP_SUNAT_VALIDATION_TOKEN);
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        rucEmisor: normalizar(rucEmisor),
        rucReceptor: normalizar(rucReceptor),
        tipoComprobante: normalizar(tipoComprobante),
        serie: normalizar(serie),
        numero: normalizar(numero),
        fechaEmision: normalizar(fechaEmision),
        total: Number(total) || 0,
        moneda: normalizar(moneda || "PEN"),
        xmlUrl: normalizar(xmlUrl),
        cdrUrl: normalizar(cdrUrl),
      }),
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      return {
        ok: false,
        estado: "OBSERVADO",
        mensaje: payload?.mensaje || "No se pudo validar con SUNAT.",
        fuente: "REMOTE",
        detalle: payload,
      };
    }

    return {
      ok: Boolean(payload?.ok ?? payload?.valido ?? false),
      estado: payload?.estado || (payload?.ok ? "VALIDO" : "OBSERVADO"),
      mensaje: payload?.mensaje || "Validación SUNAT procesada.",
      fuente: "REMOTE",
      detalle: payload,
    };
  } catch (error) {
    return {
      ok: true,
      estado: "PENDIENTE",
      mensaje: "No se pudo consultar SUNAT en línea; se mantiene validación local.",
      fuente: "LOCAL",
      detalle: { error: error?.message || "Error de red" },
    };
  }
};
